"use client"
import { useNavigate, useLocation } from "react-router-dom"
import { useEffect, useState, useMemo } from "react"
import { ClassList } from "./ClassManagement"
import { SectionList } from "./SectionManagement"
import { SubjectList } from "./SubjectManagement"
import { AcademicYearList } from "./AcademicYearManagement"
import { Settings, Book, Users, Calendar, Shield, ShieldAlert, Info, GraduationCap, Mail, MapPin, Receipt, UserCog, Building2, Clock, Search, ChevronRight, Menu, X, Wallet, CreditCard, Package } from "lucide-react"
import { AdminList } from "./Admin/Admin"
import { RoleManagement } from "./Admin/RoleManagement"
import { AboutSchool } from "./AboutSchool"
import { EmailManagement } from "./EmailManagement"
import { ReceiptCustomization } from "./ReceiptCustomization"
import AttendanceSettings from "./AttendanceSettings"
import BiometricIntegrationSettings from "./BiometricIntegrationSettings"
import SecuritySettings from "./SecuritySettings"
import { DepartmentList } from "./DepartmentManagement"
import { DesignationManagement } from "./DesignationManagement"
import { WorkShiftList } from "./WorkShiftManagement"
import { WorkShiftUserMapping } from "./WorkShiftUserMapping"
import { DepartmentUserMapping } from "./DepartmentUserMapping"
import { ShiftPlanningForm } from "./ShiftPlanningForm"
import { DesignationUserMapping } from "./DesignationUserMapping"
import { WingManagement } from "./WingManagement"
import { WingUserMapping } from "./WingUserMapping"
import { PaymentMethodsManagement } from "./PaymentMethodsManagement"
import RazorpayRouteSettings from "./RazorpayRouteSettings"
import SectionSubjectMapping from "./SectionSubjectMapping"
import StudentSubjectManager from "../student/StudentSubjectManager"
import InventorySettings from "../InventoryManagment/InventorySettings"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import axios from "axios"
import { API_ENDPOINTS } from "../../../constants/config"

export default function SettingsContent() {
  const navigate = useNavigate()
  const location = useLocation()
  const [schoolSettings, setSchoolSettings] = useState(null)
  const [schoolFeatures, setSchoolFeatures] = useState(null)
  const [isSettingsLoading, setIsSettingsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [sidebarOpen, setSidebarOpen] = useState(true)
  
  const activeTab = location.pathname === "/dashboard/settings" ? "classes" : location.pathname.split("/").pop()

  const getUserFromStorage = () => {
    try {
      const userStr = localStorage.getItem("user")
      return userStr ? JSON.parse(userStr) : null
    } catch {
      return null
    }
  }
  const user = getUserFromStorage()
  const userPermissions = user?.permissions || []
  const isAdminWithPermissions = user?.role === "staff" && userPermissions.length > 0
  const hasFullSettingsAccess = userPermissions.includes("settings")
  const hasRolesAndStaffAccess = userPermissions.includes("settings-admin")
  const canAccessSettingsTab = (tabKey) => {
    const permissionTabKey =
      tabKey === "shift-mapping"
        ? "work-shifts"
        : tabKey === "department-mapping"
          ? "departments"
          : tabKey === "designation-mapping"
            ? "designations"
            : tabKey === "wing-mapping"
              ? "wings"
             : tabKey === "shift-planning"
               ? "work-shifts"
             : tabKey === "section-subjects"
               ? "subjects"
             : tabKey === "student-subjects"
               ? "subjects"
            : tabKey
    if (tabKey === "security") return false // schooladmin/superadmin only, never staff
    if (tabKey === "roles" || tabKey === "admin") return hasRolesAndStaffAccess
    return hasFullSettingsAccess || userPermissions.includes(`settings-${permissionTabKey}`)
  }

  // Organized tab configuration with categories
  const tabCategories = [
    {
      category: "Academic",
      items: [
        { key: "classes", label: "Classes", icon: Book, description: "Manage class structures" },
        { key: "sections", label: "Sections", icon: Users, description: "Configure class sections" },
        { key: "subjects", label: "Subjects", icon: Book, description: "Subject management" },
        { key: "academic-years", label: "Academic Years", icon: Calendar, description: "Academic year setup" },
        { key: "section-subjects", label: "Section Subjects", icon: Book, description: "Assign subjects to sections" },
        { key: "student-subjects", label: "Student Subjects", icon: GraduationCap, description: "Manage student subject enrollments" },
      ]
    },
    {
      category: "Student Management",
      items: [
        { key: "attendance-settings", label: "Attendance", icon: MapPin, description: "Attendance rules & settings" },
        { key: "biometric-integration", label: "Biometric Integration", icon: Shield, description: "School biometric API key and webhook" },
      ]
    },
    {
      category: "Staff Management",
      items: [
        { key: "departments", label: "Departments", icon: Building2, description: "Department structure" },
        { key: "work-shifts", label: "Work Shifts", icon: Clock, description: "Staff shift schedules" },
        { key: "designations", label: "Designations", icon: UserCog, description: "Designation structure" },
        { key: "wings", label: "Wings", icon: Building2, description: "Wing structure" },
      ]
    },
    {
      category: "Customization",
      items: [
        { key: "receipt-customization", label: "Receipt", icon: Receipt, description: "Fee receipt templates" },
      ]
    },
    {
      category: "Financial",
      items: [
        { key: "payment-methods", label: "Payment Methods", icon: Wallet, description: "UPI, QR codes & bank accounts" },
        { key: "razorpay-route", label: "Online Payment (Razorpay)", icon: CreditCard, description: "Gateway / Razorpay route configuration" },
      ]
    },
    {
      category: "Communication",
      items: [
        { key: "email-management", label: "Email", icon: Mail, description: "Email configuration" },
      ]
    },
    {
      category: "Inventory",
      items: [
        { key: "inventory-settings", label: "Inventory Settings", icon: Package, description: "Categories, brands, units, sizes & colors" },
      ]
    },
    {
      category: "Administration",
      items: [
        { key: "roles", label: "Roles", icon: UserCog, description: "Role definitions" },
        { key: "admin", label: "Staff Permissions", icon: Shield, description: "Staff access control" },
        { key: "about", label: "About", icon: Info, description: "All information" },
        { key: "security", label: "Security", icon: ShieldAlert, description: "Force logout all devices" },
      ]
    }
  ]

  const routeOnlyTabs = useMemo(() => [
    { key: "department-mapping", label: "Department Mapping", icon: Building2, description: "Map teachers/staff to departments", category: "Staff Management" },
    { key: "shift-mapping", label: "Work Shift Mapping", icon: Clock, description: "Map teachers/staff to shifts", category: "Staff Management" },
    { key: "designation-mapping", label: "Designation Mapping", icon: UserCog, description: "Map teachers/staff to designations", category: "Staff Management" },
    { key: "wing-mapping", label: "Wing Mapping", icon: Building2, description: "Map teachers/staff to wings", category: "Staff Management" },
     { key: "shift-planning", label: "Shift Planning", icon: Calendar, description: "Assign shifts based on date ranges", category: "Staff Management" },
  ], [])

  const filteredCategories = tabCategories
    .map(category => ({
      ...category,
      items: category.items.filter(tab => {
        if (isSettingsLoading) {
          return false
        }
       
        if (schoolSettings && schoolSettings.enableSettings?.enabled === false) {
          return false
        }

        if (!schoolSettings) return true

        const sf = schoolFeatures || {}
        const hasNewFeatures = sf && Object.keys(sf).length > 0

        const fromNew = (key) => hasNewFeatures && sf[key] !== undefined
          ? sf[key]?.enabled === true
          : null

        let enabledBySettings = true
        switch (tab.key) {
          case "classes":
          case "sections":
            enabledBySettings = fromNew("classSection") ?? schoolSettings.enableClassSection?.enabled
            break
          case "about":
            enabledBySettings = fromNew("about") ?? schoolSettings.enableAbout?.enabled
            break
          case "subjects":
            enabledBySettings = fromNew("subjects") ?? schoolSettings.enableSubjects?.enabled
            break
          case "section-subjects":
            enabledBySettings = fromNew("sectionSubjectMapping") ?? fromNew("subjects") ?? schoolSettings.enableSubjects?.enabled
            break
          case "student-subjects":
            enabledBySettings = fromNew("studentSubjectEnrollment") ?? fromNew("subjects") ?? schoolSettings.enableSubjects?.enabled
            break
          case "academic-years":
            enabledBySettings = fromNew("academicYears") ?? schoolSettings.enableAcademicYears?.enabled
            break
          case "roles":
          case "admin":
            enabledBySettings = fromNew("permissions") ?? schoolSettings.enablePermissions?.enabled
            break
          case "attendance-settings":
            enabledBySettings = fromNew("attendanceSettings") ?? (
              schoolSettings.enableClockAttendance?.enabled ||
              schoolSettings.enableBiometricAttendance?.enabled ||
              schoolSettings.enableWebcamAttendance?.enabled ||
              schoolSettings.enableTeacherAttendance?.enabled ||
              schoolSettings.enableStaffAttendance?.enabled ||
              false
            )
            break
          case "biometric-integration":
            enabledBySettings = fromNew("biometricIntegration") ?? schoolSettings.enableBiometricIntegrationSettings?.enabled === true
            break
          case "payment-methods":
            enabledBySettings = fromNew("paymentMethodsSettings") ?? schoolSettings.enableFeeManagement?.enabled
            break
          case "razorpay-route":
            enabledBySettings = fromNew("razorpaySettings") ?? schoolSettings.enableFeeManagement?.enabled
            break
          case "email-management":
            enabledBySettings = fromNew("emailManagement") ?? schoolSettings.enableEmailManagement?.enabled
            break
          case "departments":
          case "department-mapping":
            enabledBySettings = fromNew("departments") ?? schoolSettings.enableDepartments?.enabled
            break
          case "designations":
          case "designation-mapping":
            enabledBySettings = fromNew("designations") ?? schoolSettings.enableDesignations?.enabled
            break
          case "work-shifts":
          case "shift-mapping":
            case "shift-planning":
            enabledBySettings = fromNew("workShifts") ?? schoolSettings.enableWorkShifts?.enabled
            break
          case "wings":
          case "wing-mapping":
            enabledBySettings = fromNew("wings") ?? schoolSettings.enableWings?.enabled ?? true
            break
          case "receipt-customization":
            enabledBySettings = fromNew("receiptCustomization") ?? schoolSettings.enableReceiptCustomization?.enabled
            break
          case "inventory-settings":
            enabledBySettings = fromNew("inventory") ?? schoolSettings.enableInventory?.enabled ?? false
            break
          default:
            enabledBySettings = true
        }

        
        const hasPermission = isAdminWithPermissions ? canAccessSettingsTab(tab.key) : true

        return enabledBySettings && hasPermission
      })
    }))
    .filter(category => category.items.length > 0)


  // Search functionality
  const searchResults = searchQuery.trim() === "" ? filteredCategories : filteredCategories.map(category => ({
    ...category,
    items: category.items.filter(tab =>
      tab.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tab.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.items.length > 0)

  useEffect(() => {
    async function fetchSettings() {
      try {
        setIsSettingsLoading(true)
        const token = localStorage.getItem("token")
        const userStr = localStorage.getItem("user")
        if (!token || !userStr) return

        const user = JSON.parse(userStr)
        const schoolId = user?.school && (typeof user.school === "object" ? user.school._id : user.school)
        if (!schoolId) {
          console.error("No school found in user data")
          return
        }

        // Fetch features from new endpoint
        const res = await axios.get(API_ENDPOINTS.schoolFeatures(schoolId), {
          headers: { Authorization: `Bearer ${token}` },
        })
       
        setSchoolSettings(res.data.data)
        setSchoolFeatures(res.data?.data?.features || {})
      } catch (error) {
        if (error.response?.status === 403) {
          setSchoolSettings(null)
          return
        }
        console.error("Error fetching school settings:", error)
      } finally {
        setIsSettingsLoading(false)
      }
    }
    fetchSettings()
  }, [])

  useEffect(() => {
    if (isSettingsLoading) return
    if (location.pathname === "/dashboard/settings" || location.pathname === "/dashboard/settings/") {
      const firstTab = filteredCategories[0]?.items[0]?.key ?? "classes"
      navigate(`/dashboard/settings/${firstTab}`, { replace: true })
    }
  }, [location.pathname, navigate, filteredCategories, isSettingsLoading])

  useEffect(() => {
    if (isSettingsLoading) return;
    
    const allAvailableTabs = filteredCategories.flatMap(cat => cat.items);
    const allowedRouteTabs = [...allAvailableTabs, ...routeOnlyTabs];
    
    // If current tab is not in allowed tabs, redirect to first available tab
    if (activeTab && !allowedRouteTabs.some((t) => t.key === activeTab)) {
      const firstTab = allAvailableTabs[0]?.key || 'classes';
      navigate(`/dashboard/settings/${firstTab}`, { replace: true });
    }
  }, [activeTab, filteredCategories, routeOnlyTabs, isSettingsLoading, navigate])

  const handleTabChange = (value) => {
    navigate(`/dashboard/settings/${value}`)
  }

  // Get current tab info for breadcrumb
  const currentTabInfo = filteredCategories
    .flatMap(cat => cat.items.map(item => ({ ...item, category: cat.category })))
    .find(tab => tab.key === activeTab)
    || routeOnlyTabs.find(tab => tab.key === activeTab)

  return (
    <div className="flex h-screen bg-gray-50">
      {isSettingsLoading && (
        <div className="absolute inset-0 z-50 bg-gray-50 flex items-center justify-center">
          <div className="text-sm text-gray-500">Loading settings...</div>
        </div>
      )}
      {/* Sidebar Navigation */}
      <aside className={cn(
        "fixed lg:static inset-y-0 left-0 w-80 bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out flex flex-col",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Sidebar Header */}
        <div className="p-2 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search settings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-50 border-gray-200 focus:bg-white"
            />
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto p-4">
          {searchResults.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No settings found matching "{searchQuery}"
            </div>
          ) : (
            <div className="space-y-6">
              {searchResults.map((category) => (
                <div key={category.category}>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-3">
                    {category.category}
                  </h3>
                  <div className="space-y-1">
                    {category.items.map((tab) => {
                      const IconComponent = tab.icon
                      const isActive = activeTab === tab.key
                      return (
                        <button
                          key={tab.key}
                          onClick={() => handleTabChange(tab.key)}
                          className={cn(
                            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 group",
                            isActive
                              ? "bg-blue-50 text-blue-700 font-medium shadow-sm"
                              : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                          )}
                        >
                          <IconComponent className={cn(
                            "h-5 w-5 flex-shrink-0 transition-transform",
                            isActive ? "text-blue-600" : "text-gray-400 group-hover:text-gray-600"
                          )} />
                          <div className="flex-1 text-left">
                            <div className="font-medium">{tab.label}</div>
                            <div className={cn(
                              "text-xs mt-0.5",
                              isActive ? "text-blue-600" : "text-gray-500"
                            )}>
                              {tab.description}
                            </div>
                          </div>
                          {isActive && (
                            <ChevronRight className="h-4 w-4 text-blue-600" />
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              {currentTabInfo && (
                <>
                  <currentTabInfo.icon className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-gray-900">{currentTabInfo.label}</span>
                </>
              )}
            </div>
            <div className="w-10" /> {/* Spacer for alignment */}
          </div>
        </header>

        {/* Content Header - Desktop */}
        <header className="hidden lg:block bg-white border-b border-gray-200 px-8 py-6">
          {currentTabInfo && (
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <currentTabInfo.icon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm text-gray-500">{currentTabInfo.category}</span>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">{currentTabInfo.label}</span>
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">{currentTabInfo.label}</h2>
                  <p className="text-sm text-gray-600 mt-1">{currentTabInfo.description}</p>
                </div>
              </div>
            </div>
          )}
        </header>

        {/* Content Body */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-4 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {activeTab === "classes" && filteredCategories.flatMap(c => c.items).some(t => t.key === "classes") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <ClassList />
              </div>
            )}
            {activeTab === "sections" && filteredCategories.flatMap(c => c.items).some(t => t.key === "sections") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <SectionList />
              </div>
            )}
            {activeTab === "subjects" && filteredCategories.flatMap(c => c.items).some(t => t.key === "subjects") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm"><SubjectList /></div>
            )}
            {activeTab === "academic-years" && filteredCategories.flatMap(c => c.items).some(t => t.key === "academic-years") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <AcademicYearList />
              </div>
            )}
            {activeTab === "receipt-customization" && filteredCategories.flatMap(c => c.items).some(t => t.key === "receipt-customization") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <ReceiptCustomization />
              </div>
            )}
            {activeTab === "email-management" && filteredCategories.flatMap(c => c.items).some(t => t.key === "email-management") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <EmailManagement />
              </div>
            )}
            {activeTab === "payment-methods" && filteredCategories.flatMap(c => c.items).some(t => t.key === "payment-methods") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <PaymentMethodsManagement />
              </div>
            )}
            {activeTab === "razorpay-route" && filteredCategories.flatMap(c => c.items).some(t => t.key === "razorpay-route") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <RazorpayRouteSettings />
              </div>
            )}
            {activeTab === "attendance-settings" && filteredCategories.flatMap(c => c.items).some(t => t.key === "attendance-settings") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <AttendanceSettings />
              </div>
            )}
            {activeTab === "biometric-integration" && filteredCategories.flatMap(c => c.items).some(t => t.key === "biometric-integration") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <BiometricIntegrationSettings />
              </div>
            )}
            {activeTab === "departments" && filteredCategories.flatMap(c => c.items).some(t => t.key === "departments") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <DepartmentList />
              </div>
            )}
            {activeTab === "department-mapping" && routeOnlyTabs.some(t => t.key === "department-mapping") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <DepartmentUserMapping />
              </div>
            )}
            {activeTab === "work-shifts" && filteredCategories.flatMap(c => c.items).some(t => t.key === "work-shifts") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <WorkShiftList />
              </div>
            )}
            {activeTab === "shift-mapping" && routeOnlyTabs.some(t => t.key === "shift-mapping") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <WorkShiftUserMapping />
              </div>
            )}
              {activeTab === "shift-planning" && routeOnlyTabs.some(t => t.key === "shift-planning") && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <ShiftPlanningForm />
                </div>
              )}
            {activeTab === "roles" && filteredCategories.flatMap(c => c.items).some(t => t.key === "roles") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <RoleManagement />
              </div>
            )}
            {activeTab === "admin" && filteredCategories.flatMap(c => c.items).some(t => t.key === "admin") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <AdminList />
              </div>
            )}
            {activeTab === "about" && filteredCategories.flatMap(c => c.items).some(t => t.key === "about") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <AboutSchool />
              </div>
            )}
            {activeTab === "security" && filteredCategories.flatMap(c => c.items).some(t => t.key === "security") && (
              <SecuritySettings />
            )}
            {activeTab === "designations" && filteredCategories.flatMap(c => c.items).some(t => t.key === "designations") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <DesignationManagement />
              </div>
            )}
            {activeTab === "designation-mapping" && routeOnlyTabs.some(t => t.key === "designation-mapping") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <DesignationUserMapping />
              </div>
            )}
            {activeTab === "wings" && filteredCategories.flatMap(c => c.items).some(t => t.key === "wings") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <WingManagement />
              </div>
            )}
            {activeTab === "wing-mapping" && routeOnlyTabs.some(t => t.key === "wing-mapping") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <WingUserMapping />
              </div>
            )}
            {activeTab === "section-subjects" && filteredCategories.flatMap(c => c.items).some(t => t.key === "section-subjects") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                <SectionSubjectMapping />
              </div>
            )}
            {activeTab === "student-subjects" && filteredCategories.flatMap(c => c.items).some(t => t.key === "student-subjects") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                <StudentSubjectManager />
              </div>
            )}
            {activeTab === "inventory-settings" && filteredCategories.flatMap(c => c.items).some(t => t.key === "inventory-settings") && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <InventorySettings hideHeader />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}
